import type { DelegationGroup } from '@contexts/delegation-groups/domain';
import { Incidence } from '@contexts/incidences/domain';
import { RoleOptions } from '@contexts/users/domain';

import type { BreadcrumbConfig } from '@components/molecules/breadcrumbs/models/breadcrumb';
import type { DynamicOptionsParams } from '@components/molecules/breadcrumbs/services/breadcrumbs.service';

import { routesMetadata } from '@shared/models/routes-metadata';
import type { AccessContext, AccessStrategy, WidgetConfig } from '@shared/services';

export class OperatorAccessStrategy implements AccessStrategy {
	getWidgets(accessContext: AccessContext): WidgetConfig[] {
		const { requestListTotal } = accessContext;

		return [
			{
				id: 'devices',
				label: 'entities.devices.title',
				routerLink: routesMetadata.devicesB2B.relative,
				icon: 'device'
			},
			{
				id: 'devices-overview',
				label: 'entities.devicesOverview.title',
				routerLink: routesMetadata.devicesOverviewB2B.relative,
				icon: 'device'
			},
			{
				id: 'devices-groups',
				label: 'entities.devicesGroups.title',
				routerLink: routesMetadata.devicesGroupsB2B.relative
			},
			{
				id: 'request-list',
				label: 'entities.requestList.widget',
				labelParams: {
					total: requestListTotal
				},
				routerLink: routesMetadata.requestList.relative,
				icon: 'requestList'
			},
			{
				id: 'incidences',
				label: 'entities.incidences.title',
				routerLink: routesMetadata.incidences.relative,
				icon: 'warning'
			}
		];
	}

	private buildBaseBreadcrumbs(): BreadcrumbConfig {
		return {
			levels: [
				{
					id: 'section',
					controlsSegmentAt: 0,
					options: [
						{
							id: 'devices',
							label: 'entities.devices.title',
							segment: routesMetadata.devicesB2B.relative,
							navigable: true
						},
						{
							id: 'devices-overview',
							label: 'entities.devicesOverview.title',
							segment: routesMetadata.devicesOverviewB2B.relative,
							navigable: true
						},
						{
							id: 'devices-groups',
							label: 'entities.devicesGroups.title',
							segment: routesMetadata.devicesGroupsB2B.relative,
							navigable: true
						},
						{
							id: 'request-list',
							label: 'entities.requestList.title',
							segment: routesMetadata.requestList.relative,
							navigable: true
						},
						{
							id: 'incidences',
							label: 'entities.incidences.title',
							segment: routesMetadata.incidences.relative,
							navigable: true
						}
					]
				},
				{
					id: 'page',
					controlsSegmentAt: 1,
					visibleWhen: {
						rootSegmentIs: routesMetadata.devicesB2B.relative,
						segmentAtIs: {
							index: 1,
							values: [routesMetadata.activateDevices.relative]
						}
					},
					options: [
						{
							id: 'activate-devices',
							label: 'entities.devices.pages.activate.title',
							segment: routesMetadata.activateDevices.relative,
							navigable: false
						}
					]
				},
				{
					id: 'devices-ids',
					controlsSegmentAt: 1,
					visibleWhen: {
						rootSegmentIs: routesMetadata.devicesB2B.relative,
						segmentAtIs: {
							index: 1,
							values: []
						}
					},
					options: []
				},
				{
					id: 'device-tabs',
					controlsSegmentAt: 2,
					visibleWhen: {
						rootSegmentIs: routesMetadata.devicesB2B.relative
					},
					options: [
						{
							id: routesMetadata.configurationB2B.relative,
							label: 'entities.devices.configuration.tab',
							segment: routesMetadata.configurationB2B.relative,
							navigable: true
						},
						{
							id: routesMetadata.callHistoriesB2B.relative,
							label: 'entities.callHistories.title',
							segment: routesMetadata.callHistoriesB2B.relative,
							navigable: true
						},
						{
							id: routesMetadata.latestPositionB2B.relative,
							label: 'entities.latestPosition.title',
							segment: 'latest-position',
							navigable: true
						},
						{
							id: routesMetadata.recordedPositions.relative,
							label: 'entities.recordedPositions.title',
							segment: 'recorded-positions',
							navigable: true
						},
						{
							id: routesMetadata.frequentPlacesB2B.relative,
							label: 'entities.frequentPlaces.title',
							segment: 'frequent-places',
							navigable: true
						},
						{
							id: routesMetadata.safeZoneB2B.relative,
							label: 'entities.safeZones.title',
							segment: routesMetadata.safeZoneB2B.relative,
							navigable: true
						},
						{
							id: routesMetadata.healthB2B.relative,
							label: 'entities.devices.actions.health',
							segment: routesMetadata.healthB2B.relative,
							navigable: true
						}
					]
				},
				{
					id: 'frequent-places-actions',
					controlsSegmentAt: 3,
					visibleWhen: {
						rootSegmentIs: 'devices-b2b',
						segmentAtIs: {
							index: 2,
							values: ['frequent-places']
						}
					},
					options: [
						{
							id: 'create',
							label: 'Crear lugar frecuente',
							segment: 'create',
							navigable: false
						}
					]
				},
				{
					id: 'groups-ids',
					controlsSegmentAt: 1,
					visibleWhen: {
						rootSegmentIs: routesMetadata.devicesGroupsB2B.relative,
						segmentAtIs: {
							index: 1,
							values: []
						}
					},
					options: []
				},
				{
					id: 'group-tabs',
					controlsSegmentAt: 2,
					visibleWhen: {
						rootSegmentIs: routesMetadata.devicesGroupsB2B.relative
					},
					options: [
						{
							id: routesMetadata.configurationGroupB2B.relative,
							label: 'entities.devicesGroups.configuration.tab',
							segment: routesMetadata.configurationGroupB2B.relative,
							navigable: true
						},
						{
							id: routesMetadata.contactsB2B.relative,
							label: 'entities.contacts.title',
							segment: routesMetadata.contactsB2B.relative,
							navigable: true
						},
						{
							id: routesMetadata.safeZoneGroupB2B.relative,
							label: 'entities.safeZones.title',
							segment: routesMetadata.safeZoneGroupB2B.relative,
							navigable: true
						},
						{
							id: routesMetadata.frequentPlacesGroupB2B.relative,
							label: 'entities.frequentPlaces.title',
							segment: routesMetadata.frequentPlacesGroupB2B.relative,
							navigable: true
						},
						{
							id: routesMetadata.manageContactsB2B.relative,
							label: 'entities.devices.deviceGroupContactLists.tab',
							segment: routesMetadata.manageContactsB2B.relative,
							navigable: true
						}
					]
				},
				{
					id: 'frequent-places-group-actions',
					controlsSegmentAt: 3,
					visibleWhen: {
						rootSegmentIs: 'devices-groups',
						segmentAtIs: {
							index: 2,
							values: ['frequent-places']
						}
					},
					options: [
						{
							id: 'create',
							label: 'entities.frequentPlaces.pages.create.title',
							segment: 'create',
							navigable: false
						}
					]
				},
				{
					id: 'incidences-ids',
					controlsSegmentAt: 1,
					visibleWhen: {
						rootSegmentIs: routesMetadata.incidences.relative,
						segmentAtIs: {
							index: 1,
							values: []
						}
					},
					options: []
				},
				{
					id: 'incidence-tabs',
					controlsSegmentAt: 2,
					visibleWhen: {
						rootSegmentIs: routesMetadata.incidences.relative
					},
					options: [
						{
							id: routesMetadata.incidenceManagement.relative,
							label: 'entities.incidences.pages.management.tab',
							segment: routesMetadata.incidenceManagement.relative,
							navigable: true
						}
					]
				}
			]
		};
	}

	async getBreadcrumbs(
		accessContext: AccessContext
	): Promise<[BreadcrumbConfig, DynamicOptionsParams[]]> {
		const {
			user,
			findAllDevicesUseCase,
			findAllDevicesGroupsUseCase,
			findAllIncidencesByDelegationIdUseCase
		} = accessContext;
		const baseConfig = this.buildBaseBreadcrumbs();

		const devices = await findAllDevicesUseCase({});
		let devicesGroups: DelegationGroup[] = [];
		let incidences: Incidence[] = [];

		if (user.delegationId) {
			const responseDevicesGroups = await findAllDevicesGroupsUseCase({
				delegationId: user.delegationId
			});
			devicesGroups = responseDevicesGroups.items;

			const responceIncidences = await findAllIncidencesByDelegationIdUseCase({
				delegationId: user.delegationId
			});
			incidences = responceIncidences.items;
		}

		if (devices.total === 0 && devicesGroups.length === 0) return [baseConfig, []];

		const devicesDynamicOptions: DynamicOptionsParams = {
			levelId: 'devices-ids',
			options: devices.items.map((device) => ({
				id: device.id,
				label: device.carrierName,
				segment: device.id,
				navigable: true
			})),
			preserveChildrenInGroupId: 'device-ids'
		};

		const groupsDynamicOptions: DynamicOptionsParams = {
			levelId: 'groups-ids',
			options: devicesGroups.map((group) => ({
				id: group.id,
				label: group.name,
				segment: group.id,
				navigable: true
			})),
			preserveChildrenInGroupId: 'group-ids'
		};

		const incidencesDynamicOptions: DynamicOptionsParams = {
			levelId: 'incidences-ids',
			options: incidences.map((incidence) => ({
				id: incidence.id,
				label: incidence.deviceIdentificator,
				segment: incidence.id,
				navigable: true
			})),
			preserveChildrenInGroupId: 'incidence-ids'
		};

		return [
			baseConfig,
			[devicesDynamicOptions, groupsDynamicOptions, incidencesDynamicOptions]
		];
	}

	canAccess(roles: string[]): boolean {
		return roles.includes(RoleOptions.OPERATOR);
	}

	canWrite(): boolean {
		return true;
	}

	can(): boolean {
		return false;
	}
}
